/* The domain card of the card library, also dealt by the character builder. */

import { ComponentPropsWithRef, CSSProperties } from 'react';
import type { DomainCard } from '../content/domains/cards';
import { DOMAIN_COLORS } from '../content/domains/data';
import { DomainIcon } from './DomainIcon';
import './cards.css';

export function ZapIcon({ size = 12 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size} height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="zap-icon"
    >
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

/* ── The card itself, as the modal shows it; Character Creation deals the same card ── */
interface DomainCardSheetProps extends ComponentPropsWithRef<'div'> {
  card: DomainCard;
  /** "3 / 189" in the footer, when browsing */
  counter?: string;
  titleId?: string;
}

export function DomainCardSheet({ card, counter, titleId, children, style, ...rest }: DomainCardSheetProps) {
  const color = (DOMAIN_COLORS as Record<string, string>)[card.domain] || '#888';
  return (
    <div
      {...rest}
      className="card-sheet"
      data-domain={card.domain.toLowerCase()}
      style={{ '--domain-color': color, ...style } as CSSProperties}
    >
      {/* ── Background icon watermark ── */}
      <div className="card-sheet__bg" aria-hidden="true">
        <DomainIcon domain={card.domain} className="card-sheet__icon" />
      </div>

      {/* ── Corner badges ── */}
      <div className="card-sheet__level" aria-label={`Level ${card.level}`}>
        <svg className="card-sheet__level-svg" viewBox="0 0 44 64" preserveAspectRatio="none" aria-hidden="true">
          <path d="M1 -2 L43 -2 L43 48 L22 63 L1 48 Z"
            fill="var(--bg-card)"
            stroke="var(--domain-color, #888)"
            strokeWidth="2"
          />
        </svg>
        <span className="card-sheet__level-num">{card.level}</span>
      </div>
      <div className="card-sheet__recall-ring" aria-label={card.recallCost === 0 ? 'Free Recall' : `Recall cost ${card.recallCost}`}>
        {card.recallCost > 0 ? <><ZapIcon size={10} />{card.recallCost}</> : <span className="card-sheet__free">0<ZapIcon size={9} /></span>}
      </div>

      {/* ── Foreground content ── */}
      <div className="card-sheet__type">{card.type}</div>
      <h2 className="card-sheet__name" id={titleId}>{card.name}</h2>
      <div className="card-sheet__rule" aria-hidden="true" />
      <div className="card-sheet__body">{card.rendered}</div>

      {children}

      {/* ── Footer ── */}
      <div className="card-sheet__footer">
        <span>{card.domain} Domain</span>
        <span className="card-sheet__footer-dot">·</span>
        <span>Level {card.level}</span>
        {counter && (
          <>
            <span className="card-sheet__footer-dot">·</span>
            <span className="card-sheet__counter">{counter}</span>
          </>
        )}
        <span className="card-sheet__footer-dot">·</span>
        <span>dhtools.net</span>
      </div>
    </div>
  );
}
